export type LabelGroupId =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'gray'

export interface LabelGroup {
  id: LabelGroupId
  /** Shown in the picker and the tab's filter chips. */
  name: string
  /** Band border + title chip border. */
  accent: string
  /** Band fill at rest. */
  fill: string
  /** Band fill while focused/hovered, and the title chip's focused background. */
  fillFocused: string
}

/**
 * The seven fixed label groups.
 *
 * Hues are spread far enough apart to stay separable at a band's low fill
 * alpha, and each accent clears 5.8:1 against the near-black chart surface. The
 * blue is a cyan-leaning azure rather than an indigo so a grouped label never
 * reads as the ruler's `CHART_COLORS.CURSOR_ACCENT` band; the orange sits below
 * the ungrouped default amber (`#e8a33d`) and the yellow above it. Fill alphas
 * mirror that amber's 0.1 / 0.2 ratios.
 */
export const LABEL_GROUPS: readonly LabelGroup[] = [
  {
    id: 'red',
    name: 'Red',
    accent: '#e7555a',
    fill: 'rgba(231, 85, 90, 0.1)',
    fillFocused: 'rgba(231, 85, 90, 0.2)',
  },
  {
    id: 'orange',
    name: 'Orange',
    accent: '#ec7c3c',
    fill: 'rgba(236, 124, 60, 0.1)',
    fillFocused: 'rgba(236, 124, 60, 0.2)',
  },
  {
    id: 'yellow',
    name: 'Yellow',
    accent: '#eac73e',
    fill: 'rgba(234, 199, 62, 0.1)',
    fillFocused: 'rgba(234, 199, 62, 0.2)',
  },
  {
    id: 'green',
    name: 'Green',
    accent: '#45c485',
    fill: 'rgba(69, 196, 133, 0.1)',
    fillFocused: 'rgba(69, 196, 133, 0.2)',
  },
  {
    id: 'blue',
    name: 'Blue',
    accent: '#30aee8',
    fill: 'rgba(48, 174, 232, 0.1)',
    fillFocused: 'rgba(48, 174, 232, 0.2)',
  },
  {
    id: 'purple',
    name: 'Purple',
    accent: '#c77be0',
    fill: 'rgba(199, 123, 224, 0.1)',
    fillFocused: 'rgba(199, 123, 224, 0.2)',
  },
  {
    id: 'gray',
    name: 'Gray',
    accent: '#a0a5b1',
    fill: 'rgba(160, 165, 177, 0.1)',
    fillFocused: 'rgba(160, 165, 177, 0.2)',
  },
]

const LABEL_GROUPS_BY_ID = new Map<string, LabelGroup>(
  LABEL_GROUPS.map((group) => [group.id, group])
)

/**
 * Resolve a stored group id. Returns `null` for anything unrecognized —
 * including labels written before groups existed — so a stale or hand-edited
 * `group_id` renders as ungrouped instead of failing.
 */
export const getLabelGroup = (
  id: string | null | undefined
): LabelGroup | null => (id ? (LABEL_GROUPS_BY_ID.get(id) ?? null) : null)

export const isLabelGroupId = (id: unknown): id is LabelGroupId =>
  typeof id === 'string' && LABEL_GROUPS_BY_ID.has(id)
